/**
 * MagicBI Design System - Shadow Tokens
 *
 * Elevation system with consistent shadow values
 * for creating depth and hierarchy in the interface.
 */

// Base shadow values
export const baseShadows = {
  // Light theme shadows
  light: {
    lg_v1:
      "-2px 10px 8px 0 rgba(0, 0, 0, 0.04), -2px 2px 3px 0 rgba(0, 0, 0, 0.06)",
    xs: "0 1px 2px 0 rgba(25, 18, 54, 0.05)",
    sm: "0 1px 3px 0 rgba(25, 18, 54, 0.1), 0 1px 2px 0 rgba(25, 18, 54, 0.06)",
    md: "0 4px 6px -1px rgba(25, 18, 54, 0.1), 0 2px 4px -1px rgba(25, 18, 54, 0.06)",
    lg: "0 10px 15px -3px rgba(25, 18, 54, 0.1), 0 4px 6px -2px rgba(25, 18, 54, 0.05)",
    xl: "0 20px 25px -5px rgba(25, 18, 54, 0.1), 0 10px 10px -5px rgba(25, 18, 54, 0.04)",
    "2xl": "0 25px 50px -12px rgba(25, 18, 54, 0.25)",
    sectionHeader: "1px 1px 6px 0 rgba(89, 98, 112, 0.10)",
  },

  // Dark theme shadows
  dark: {
    xs: "0 1px 2px 0 rgba(0, 0, 0, 0.3)",
    sm: "0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.25)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.6)",
  },
} as const;

// Semantic shadow tokens
export const shadows = {
  none: "none",

  // Elevation levels
  elevation: {
    1: baseShadows.light.xs, // Subtle elevation (cards on page)
    2: baseShadows.light.sm, // Low elevation (raised buttons)
    3: baseShadows.light.md, // Medium elevation (dropdowns)
    4: baseShadows.light.lg, // High elevation (modals)
    5: baseShadows.light.xl, // Very high elevation (tooltips)
    6: baseShadows.light["2xl"], // Maximum elevation (overlays)
  },

  // Interactive states
  interactive: {
    hover: baseShadows.light.md,
    focus: "0 0 0 3px rgba(100, 66, 179, 0.12)", // Brand color with low opacity
    active: baseShadows.light.sm,
  },

  // Component-specific shadows
  component: {
    button: {
      default: baseShadows.light.sm,
      hover: baseShadows.light.md,
      active: baseShadows.light.xs,
    },
    card: {
      default: baseShadows.light.sm,
      hover: baseShadows.light.md,
      control_panel: baseShadows.light.lg_v1,
      sectionHeader: baseShadows.light.sectionHeader,
      /** AI summary / follow-up insight card — Figma 4306:346315 */
      ai: "2px 2px 8px 0 rgba(89, 98, 112, 0.25)",
      /** Create-story AI method card — Figma 5326:88032. */
      storyCreationAi: "1px 1px 6px 0 rgba(89, 98, 112, 0.06)",
      /** Blank-canvas story method card — Figma 5326:88021. */
      storyCreationBlank: "1px 1px 3px 0 rgba(89, 98, 112, 0.06)",
    },
    /** KG data source node — Figma: 3px 3px 8px amber at 10% opacity */
    kgDataSource: "3px 3px 8px 0 rgba(245, 158, 11, 0.1)",
    /** Story present-page top bar — Figma: 0 2px 3px structural at 15% opacity */
    presentHeader: "0 2px 3px 0 rgba(89, 98, 112, 0.15)",
    /**
     * Form-modal header divider shadow — Figma: 0 2px 6px structural at 15%.
     * Same colour and alpha as `presentHeader`, softer blur; separate token
     * because it is a different surface, not a variant of the same one.
     */
    modalHeader: "0 2px 6px 0 rgba(89, 98, 112, 0.15)",
    /** Create-story recipe modal header — Figma 5326:88439. */
    createStoryModalHeader: "0 2px 3px 0 rgba(89, 98, 112, 0.15)",
    /** Create-story recipe modal shell — Figma 5326:88009. */
    createStoryModal: "0 4px 20px 0 rgba(89, 98, 112, 0.2)",
    /**
     * Range-slider thumb — Figma 6409:556282, from the Ellipse 55 drop-shadow
     * filter (dx 1, dy 1, stdDeviation 2 → 4px blur, rgb(89,98,112) at 15%).
     */
    sliderThumb: "1px 1px 4px 0 rgba(89, 98, 112, 0.15)",
    /**
     * Chart point action floating menu — Figma 6590:210643 "Floating nav".
     * Same geometry/alpha as `sliderThumb`; separate token because it is a
     * different surface (a floating menu), mirroring the modalHeader precedent.
     */
    pointActionMenu: "1px 1px 4px 0 rgba(89, 98, 112, 0.15)",
    /** Present-mode slide filmstrip — Figma: 0 5px 14px black at 15% opacity */
    filmstrip: "0 5px 14px 0 rgba(0, 0, 0, 0.15)",
    dropdown: baseShadows.light.lg,
    modal: baseShadows.light["2xl"],
    /** Command palette / search overlay (story redesign F20): 0 20px 40px -8px ink at 22% */
    palette: "0 20px 40px -8px rgba(24, 16, 43, 0.22)",
    tooltip: baseShadows.light.xl,
    popover: baseShadows.light.lg,
    /** Floating menu / panel overlay: 0 20px 40px -8px ink at 36% */
    overlayMenu: "0 20px 40px -8px rgba(24, 16, 43, 0.36)",
    /** Right-edge side sheet / drawer (cast to the left): -20px 0 40px -8px ink at 18% */
    drawerRight: "-20px 0 40px -8px rgba(24, 16, 43, 0.18)",
  },

  // Status shadows (colored shadows for status indicators)
  status: {
    success:
      "0 0 0 1px rgba(27, 190, 101, 0.2), 0 2px 4px 0 rgba(27, 190, 101, 0.1)",
    warning:
      "0 0 0 1px rgba(251, 151, 91, 0.2), 0 2px 4px 0 rgba(251, 151, 91, 0.1)",
    error:
      "0 0 0 1px rgba(255, 10, 128, 0.2), 0 2px 4px 0 rgba(255, 10, 128, 0.1)",
    info: "0 0 0 1px rgba(28, 174, 200, 0.2), 0 2px 4px 0 rgba(28, 174, 200, 0.1)",
  },
} as const;

// Dark theme shadows
export const darkShadows = {
  none: "none",

  // Elevation levels
  elevation: {
    1: baseShadows.dark.xs,
    2: baseShadows.dark.sm,
    3: baseShadows.dark.md,
    4: baseShadows.dark.lg,
    5: baseShadows.dark.xl,
    6: baseShadows.dark["2xl"],
  },

  // Interactive states
  interactive: {
    hover: baseShadows.dark.md,
    focus: "0 0 0 3px rgba(170, 151, 219, 0.2)", // Brand light color with low opacity
    active: baseShadows.dark.sm,
  },

  // Component-specific shadows
  component: {
    button: {
      default: baseShadows.dark.sm,
      hover: baseShadows.dark.md,
      active: baseShadows.dark.xs,
    },
    card: {
      default: baseShadows.dark.sm,
      hover: baseShadows.dark.md,
    },
    dropdown: baseShadows.dark.lg,
    modal: baseShadows.dark["2xl"],
    palette: "0 20px 40px -8px rgba(24, 16, 43, 0.22)",
    tooltip: baseShadows.dark.xl,
    popover: baseShadows.dark.lg,
  },

  // Status shadows
  status: {
    success:
      "0 0 0 1px rgba(27, 190, 101, 0.3), 0 2px 4px 0 rgba(27, 190, 101, 0.2)",
    warning:
      "0 0 0 1px rgba(251, 151, 91, 0.3), 0 2px 4px 0 rgba(251, 151, 91, 0.2)",
    error:
      "0 0 0 1px rgba(255, 10, 128, 0.3), 0 2px 4px 0 rgba(255, 10, 128, 0.2)",
    info: "0 0 0 1px rgba(28, 174, 200, 0.3), 0 2px 4px 0 rgba(28, 174, 200, 0.2)",
  },
} as const;

// Special effects shadows
export const effectShadows = {
  // Glow effects
  glow: {
    primary: "0 0 20px rgba(100, 66, 179, 0.3)",
    success: "0 0 20px rgba(27, 190, 101, 0.3)",
    warning: "0 0 20px rgba(251, 151, 91, 0.3)",
    error: "0 0 20px rgba(255, 10, 128, 0.3)",
    info: "0 0 20px rgba(28, 174, 200, 0.3)",
  },

  // Inner shadows
  inner: {
    subtle: "inset 0 1px 2px 0 rgba(25, 18, 54, 0.05)",
    strong: "inset 0 2px 4px 0 rgba(25, 18, 54, 0.1)",
  },

  // AI-specific shadows (for AI elements)
  ai: {
    glow: "0 0 30px rgba(100, 66, 179, 0.2), 0 0 60px rgba(170, 151, 219, 0.1)",
    subtle: "0 2px 8px rgba(100, 66, 179, 0.15)",
  },
} as const;

// Z-index scale for layering
export const zIndex = {
  hide: -1,
  auto: "auto",
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
} as const;

// Type definitions
export type ShadowTokens = typeof shadows;
export type DarkShadowTokens = typeof darkShadows;
export type EffectShadows = typeof effectShadows;
export type ZIndex = typeof zIndex;

// Utility functions
export const getShadow = (category: keyof ShadowTokens, variant?: string) => {
  if (variant && typeof shadows[category] === "object") {
    return (shadows[category] as any)[variant];
  }
  return shadows[category];
};

export const getDarkShadow = (
  category: keyof DarkShadowTokens,
  variant?: string,
) => {
  if (variant && typeof darkShadows[category] === "object") {
    return (darkShadows[category] as any)[variant];
  }
  return darkShadows[category];
};

// CSS helper for styled-components
export const shadowMixin = {
  elevation: (level: keyof typeof shadows.elevation) => `
    box-shadow: ${shadows.elevation[level]};
  `,

  elevationDark: (level: keyof typeof darkShadows.elevation) => `
    box-shadow: ${darkShadows.elevation[level]};
  `,

  focus: () => `
    box-shadow: ${shadows.interactive.focus};
  `,

  focusDark: () => `
    box-shadow: ${darkShadows.interactive.focus};
  `,

  glow: (color: keyof typeof effectShadows.glow) => `
    box-shadow: ${effectShadows.glow[color]};
  `,
};
